import { useAttachments } from '$src/lib/hooks/useAttachments'
import { maintenanceLogHooks } from '$src/lib/trpcHooks'
import { useLingui } from '@lingui/react/macro'
import { notifications } from '@mantine/notifications'
import { AppModal } from '@markku/components'
import { useState } from 'react'
import { SystemTaskDetails } from './SystemTaskDetails'
import { SystemTaskFormData } from './SystemTaskDetails/types'

import type {
  AttachmentResponse,
  MaintenanceLogWithRelations,
} from '@markku/types'

export interface SystemTaskDetailsData {
  id: string
  title: string
  description?: string
  category?: string
  estimatedDuration?: string
  isUserTask: boolean
  completed: boolean
  attachments?: AttachmentResponse[]
  log?: MaintenanceLogWithRelations
}

interface SystemTaskDetailsModalProps {
  onClose: () => void
  task: SystemTaskDetailsData
  propertyId: number
  hasPackage: boolean
  onSave?: () => void
}

export function SystemTaskDetailsModal({
  onClose,
  task,
  propertyId,
  hasPackage,
  onSave,
}: SystemTaskDetailsModalProps) {
  const { t } = useLingui()
  const [isSaving, setIsSaving] = useState(false)
  // Use task.completed which is year-specific (not task.log which may be undefined for this season-year)
  const [markAsCompleted, setMarkAsCompleted] = useState(task.completed)

  const [formData, setFormData] = useState<SystemTaskFormData>({
    notes: task.log?.notes || '',
    completedDate:
      task.log?.completedDate || new Date().toISOString().split('T')[0],
  })

  // Initialize attachments hook with log attachments
  const attachments = useAttachments('maintenance_log', task.log?.attachments)

  const { mutateAsync: createLog } = maintenanceLogHooks.useCreate()
  const { mutateAsync: updateLog } = maintenanceLogHooks.useUpdate()

  // Determine if this is a Markku task (not a user task)
  const isMarkkuTask = !task.isUserTask

  const validateForm = () => {
    if (markAsCompleted && !formData.completedDate) {
      notifications.show({
        title: t`Error`,
        message: t`Completion date is required when marking task as completed`,
        color: 'red',
      })
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setIsSaving(true)
    try {
      const taskId = parseInt(task.id)

      if (task.log?.id) {
        // Update existing log with attachments
        await updateLog({
          id: task.log.id,
          data: {
            notes: formData.notes,
            completedDate: markAsCompleted ? formData.completedDate : null,
          },
          attachments: attachments.getChanges(),
        })
      } else {
        // Create new log with attachments
        await createLog({
          data: {
            propertyId,
            maintenanceTaskId: taskId,
            taskNameSnapshot: task.title,
            descriptionSnapshot: task.description,
            completedDate: markAsCompleted ? formData.completedDate : null,
            // Note: completedByUserId is auto-populated by backend via contextFields
            notes: formData.notes || null,
          },
          attachments: attachments.getChanges(),
        })
      }

      notifications.show({
        title: t`Success`,
        message: markAsCompleted ? t`Task marked as completed` : t`Notes saved`,
        color: 'green',
      })

      onSave?.()
      onClose()
    } catch (error) {
      console.error('Error saving system task:', error)
      notifications.show({
        title: t`Error`,
        message: t`Save failed`,
        color: 'red',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <AppModal
      opened={true}
      onClose={onClose}
      title={t`Task to be handled`}
      confirmProps={
        isMarkkuTask
          ? undefined
          : {
              label: t`Save`,
              onClick: handleSave,
              loading: isSaving,
            }
      }
      cancelProps={{
        label: t`Cancel`,
      }}
      showFooter={true}
    >
      <SystemTaskDetails
        task={task}
        formData={formData}
        onFormDataChange={setFormData}
        markAsCompleted={markAsCompleted}
        onMarkAsCompletedChange={setMarkAsCompleted}
        disabled={isSaving}
        hasPackage={hasPackage}
        attachments={attachments}
      />
    </AppModal>
  )
}
